<template>
    <div class="currency-input">
        <input :disabled="props.disabled" ref="inputRef" type="text" v-model="displayValue" @blur="onBlur" @input="onInput" @focus="onFocus" />
    </div>
</template>

<script>
import { defineComponent, nextTick, ref, watch } from 'vue'

export default defineComponent({
    name: 'CurrencyInput',
    props: {
        modelValue: {
            type: [Number, String],
            default: 0.00
        },
        type: {
            type: String,
            default: 'currency' // or 'percentage'
        },
        disabled: {
            type:Boolean,
            default: false
        },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }) {
        const inputRef = ref(null)
        const displayValue = ref('')

        const currencyFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })

        function formatValue(val) {
            const num = parseFloat(val)
            if (isNaN(num)) return props.type === 'percentage' ? '0%' : '$0.00'

            if (props.type === 'percentage') {
                const percentage = num
                const userTypedDecimal =
                    (displayValue.value && displayValue.value.includes('.')) ||
                    !Number.isInteger(percentage)

                const fractionDigits = userTypedDecimal ? 2 : 0
                const formatted = percentage.toFixed(fractionDigits)

                // Remove commas manually (if any, based on locale)
                return formatted.replace(/,/g, '') + '%'
            }

            return currencyFormatter.format(num)
        }


        // Initial formatting
        displayValue.value = props.type === 'percentage' ? String(props.modelValue || '') : formatValue(props.modelValue)

        // Watch for external modelValue changes
        watch(() => props.modelValue, (newVal) => {
            displayValue.value = props.type === 'percentage' ? String(newVal || '') : formatValue(newVal)
        })

        // Watch for type changes
        watch(() => props.type, () => {
            displayValue.value = props.type === 'percentage' ? String(props.modelValue || '') : formatValue(props.modelValue)
        })

        function onInput(e) {
            if (props.type === 'percentage') {
                // For percentage, show exactly what user types
                displayValue.value = e.target.value
            } else {
                // For currency, clean and show raw numbers
                const raw = e.target.value.replace(/[^\d.]/g, '')
                displayValue.value = raw
            }
        }

        function onBlur() {
            const trimmed = displayValue.value.trim()
            if (trimmed === '') {
                displayValue.value = formatValue(props.modelValue)
                return
            }

            if (props.type === 'percentage') {
                // For percentage, keep the raw input and emit the raw value
                const parsed = parseFloat(trimmed)
                const finalValue = isNaN(parsed) ? parseFloat(props.modelValue) : parsed
                emit('update:modelValue', finalValue)
                emit('change', finalValue)
                // Don't format - keep displaying what user typed
                return
            }

            // For currency, apply formatting
            const parsed = parseFloat(trimmed)
            const finalValue = isNaN(parsed) ? parseFloat(props.modelValue) : parsed

            displayValue.value = formatValue(finalValue)
            emit('update:modelValue', finalValue)
            emit('change', finalValue)
        }

        function onFocus() {
            nextTick(() => {
                if (inputRef.value) {
                    inputRef.value.select()
                }
            })
        }

        watch(() => props.type, () => {
            const resetValue = 0
            displayValue.value = formatValue(resetValue)
            emit('update:modelValue', resetValue)
            emit('change', resetValue)
        })

        return {
            displayValue,
            inputRef,
            onInput,
            onBlur,
            onFocus,
            props
        }
    }
})
</script>

<style scoped>
.currency-input {
    display: flex;
    align-items: center;
    border: 1px solid #ced4da;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    border-radius: 3px;
}

.currency-input.hasError{
    border: 2px solid #dc3545;
}
input {
    width: 100%;
    padding: 4px 0;
    outline: none;
    border: none;
}
</style>
